/**
 * The bulk-import write gate: does this preview row get written?
 *
 * Pulled out of `BulkImport.tsx`'s import loop so the rule is pinned by a test,
 * because it was broken once in exactly the way a later edit could reintroduce.
 * The loop used to OR in a "self-heal": an unticked imageless row was written
 * anyway when its existing ref carried a `pendingImageUrl`. The preview showed
 * those rows unchecked, so nothing on screen said they would land — and 163
 * Glycine heritage refs were imported with no image on the strength of saved
 * URLs from a dead scrape that never resolved.
 *
 * The rule now is the checkbox, and only the checkbox. `existingRef` is accepted
 * precisely so the test can hand it a stale `pendingImageUrl` and prove it
 * changes nothing. The recovery path survives where it belongs — inside
 * `importSingle`, for rows the admin chose to tick.
 */
export function shouldWritePreviewItem(
  item: { included: boolean },
  _existingRef?: { pendingImageUrl?: string; images?: unknown[] } | null,
): boolean {
  return item.included;
}

/**
 * Why an imageless row starts unticked, shown in the row's ⚠ warning and on its
 * checkbox.
 *
 * Deliberately says nothing about whether an earlier saved URL will work: the
 * preview cannot know. Whether a `pendingImageUrl` resolves is only discovered
 * by fetching it during the import, and SKU-keyed imports don't read the
 * existing refs until then. Claiming "didn't resolve" here would be inventing it.
 */
export const NO_IMAGE_REASON =
  "No image in this import — left unticked, so it won't be imported. Tick to import it anyway; a saved image URL from an earlier import will be retried.";
